import escochexLogo from '../../../assets/escochex-logo.webp';
import { formatDate, formatDateTime, formatMoney, servicePeriodText } from '../format';
import type { PaymentStatement } from '../types';
import { StatusBadge } from './StatusBadge';

/**
 * The Contractor Payment Statement: ONE document component that drives the on-screen preview and the print output (and the shape the server-side PDF will
 * render next). It replaces the check-shaped body of the Figma frames (authorized override): there is no payee/amount-in-words/signature block, so it can't be
 * mistaken for a negotiable check, and a faint "THIS IS NOT A CHECK" watermark is always present. Voided adds the reason banner and exactly three red
 * VOIDED watermarks (one large, two smaller). No bank, routing or card number, no tax or withholding, is ever shown.
 */
export function StatementDocument({ statement, fallbackCompanyName }: { statement: PaymentStatement; fallbackCompanyName: string | null }) {
  const s = statement;
  const isVoided = s.status === 'Voided';
  const company = s.company?.name?.trim() || fallbackCompanyName;
  const statusDate = s.status === 'Paid' && s.paid ? `Paid on ${formatDate(s.paid.paidOn)}` : null;
  const hoursLines = s.lines.some((l) => l.amountMode === 'HoursRate');
  return (
    <article className={`cp-statement cp-statement--${s.status.toLowerCase()}`} data-status={s.status} aria-label={`Contractor payment statement ${s.paymentId}`}>
      {s.notACheckWatermark && <span className="cp-watermark cp-watermark--notcheck" aria-hidden="true" data-testid="watermark-not-a-check">THIS IS NOT A CHECK</span>}
      {isVoided && (
        <>
          <span className="cp-watermark cp-watermark--void cp-watermark--void-top" aria-hidden="true" data-testid="watermark-voided">VOIDED</span>
          <span className="cp-watermark cp-watermark--void cp-watermark--void-center" aria-hidden="true" data-testid="watermark-voided">VOIDED</span>
          <span className="cp-watermark cp-watermark--void cp-watermark--void-bottom" aria-hidden="true" data-testid="watermark-voided">VOIDED</span>
        </>
      )}

      {isVoided && s.void && (
        <div className="cp-void-banner" role="note" data-testid="void-banner">
          <p className="cp-void-banner__head">
            <strong>This payment has been voided and is retained for recordkeeping.</strong>
            <span>Voided on {formatDateTime(s.void.atUtc)} by {s.void.byEmail}</span>
          </p>
          <p className="cp-void-banner__reason">Reason: {s.void.reason}</p>
        </div>
      )}

      <header className="cp-statement__header">
        <div className="cp-statement__brand">
          <img className="cp-statement__logo" src={escochexLogo} alt="Escochex" />
          <h3>{s.title}</h3>
        </div>
        <div className="cp-statement__status">
          <StatusBadge status={s.status} variant="statement" />
          <p>Payment ID: {s.paymentId}</p>
          {statusDate && <p>{statusDate}</p>}
          {s.batchReference && <p>Batch {s.batchReference}</p>}
        </div>
      </header>

      <div className="cp-statement__parties">
        <div>
          <h4>Paid by</h4>
          <p className="cp-statement__strong">{company ?? 'Company name not available'}</p>
          {s.company?.legalName && s.company.legalName !== company && <p>{s.company.legalName}</p>}
          {s.company?.address && <p>{s.company.address}</p>}
        </div>
        <div>
          <h4>Contractor</h4>
          <p className="cp-statement__strong">{s.contractor.name}</p>
          {s.contractor.businessName && <p>{s.contractor.businessName}</p>}
        </div>
      </div>

      <dl className="cp-statement__meta" aria-label="Payment details">
        <div><dt>Payment Date</dt><dd>{formatDate(s.paymentDate)}</dd></div>
        <div><dt>Service Period</dt><dd>{servicePeriodText(s.servicePeriodStart, s.servicePeriodEnd)}</dd></div>
        <div><dt>Payment Method</dt><dd>{s.paymentMethodLabel}{s.checkNumber ? ` · Check No. ${s.checkNumber}` : ''}</dd></div>
        <div><dt>Source</dt><dd>{s.provenance === 'Outside Escochex' ? 'Paid outside Escochex (recorded here)' : 'Escochex'}</dd></div>
        {s.reference && <div><dt>Reference</dt><dd>{s.reference}</dd></div>}
        {s.paid?.reference && <div><dt>Paid note</dt><dd>{s.paid.reference}</dd></div>}
      </dl>

      <section aria-labelledby={`${s.paymentId}-lines`} className="cp-statement__lines">
        <h4 id={`${s.paymentId}-lines`}>Payment Details</h4>
        <div className="table-scroll">
          <table aria-label="Payment line items">
            <thead>
              <tr>
                <th scope="col">Description</th>
                {hoursLines && <th scope="col" className="cp-num">Hours</th>}
                {hoursLines && <th scope="col" className="cp-num">Rate</th>}
                <th scope="col" className="cp-num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {s.lines.length > 0 ? (
                s.lines.map((l, i) => (
                  <tr key={i}>
                    <th scope="row">{l.description}</th>
                    {hoursLines && <td className="cp-num">{l.amountMode === 'HoursRate' && l.hours !== null ? l.hours : '—'}</td>}
                    {hoursLines && <td className="cp-num">{l.amountMode === 'HoursRate' && l.rate !== null ? formatMoney(l.rate) : '—'}</td>}
                    <td className="cp-num">{formatMoney(l.amount)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <th scope="row">{s.description ?? s.memo ?? 'Contractor payment'}</th>
                  <td className="cp-num">{formatMoney(s.amount)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={hoursLines ? 3 : 1}>Payment Amount</th>
                <td className="cp-num cp-statement__total" data-testid="statement-amount">{formatMoney(s.amount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {s.memo && (
        <section className="cp-statement__memo" aria-label="Memo">
          <h4>Memo</h4>
          <p>{s.memo}</p>
        </section>
      )}

      <footer className="cp-statement__foot">
        <p>This statement is a record of a contractor payment. It is not a check, is not negotiable, and shows no tax withholding.</p>
        <p>Generated {formatDateTime(s.generatedAtUtc)}{s.isPreview ? ' · staging preview with synthetic data' : ''}</p>
      </footer>
    </article>
  );
}
